package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.*
import com.sddamico.models.*
import com.sddamico.state.LocalAuthState
import com.sddamico.util.formatPoints
import com.sddamico.util.sportDisplayName
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

val TradeRosterGridStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(2.cssRem)
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "1fr 1fr")
                property("align-items", "start")
            }
            .gap(2.cssRem)
    }
}

val TradePlayerGridStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.5.cssRem)
            .fillMaxWidth()
    }
}

@InitRoute
fun initTrade(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Create Trade Offer", wideContent = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun TradePage() {
    val authState = LocalAuthState.current
    val ctx = rememberPageContext()
    val receiverClubId = ctx.route.params["receiverClubId"]
    val preselectedPlayerId = ctx.route.params["playerId"]

    // Auth guard
    LaunchedEffect(authState.loading, authState.user) {
        if (!authState.loading && authState.user == null) {
            ctx.router.tryRoutingTo("/sign-in?returnTo=/trade")
        }
    }

    if (authState.loading || authState.user == null) {
        PageLoading()
        return
    }

    val userClubId = authState.user.clubId
    if (userClubId == null || receiverClubId == null) {
        ErrorCard("Missing required trade parameters")
        return
    }

    var senderRoster by remember { mutableStateOf<ClubRosterResponse?>(null) }
    var receiverRoster by remember { mutableStateOf<ClubRosterResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Selection state
    val selectedSenderPlayerIds = remember { mutableStateListOf<String>() }
    val selectedReceiverPlayerIds = remember { mutableStateListOf<String>() }
    val selectedSenderPickIds = remember { mutableStateListOf<String>() }
    val selectedReceiverPickIds = remember { mutableStateListOf<String>() }

    var submitting by remember { mutableStateOf(false) }
    var submitError by remember { mutableStateOf<String?>(null) }

    // Load both rosters
    LaunchedEffect(Unit) {
        try {
            val leagueYear = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
            val lyId = leagueYear.id
            senderRoster = apiGet(
                "/api/clubs/roster?clubId=$userClubId&leagueYearId=$lyId",
                ClubRosterResponse.serializer()
            )
            receiverRoster = apiGet(
                "/api/clubs/roster?clubId=$receiverClubId&leagueYearId=$lyId",
                ClubRosterResponse.serializer()
            )
            // Pre-select player if specified
            if (preselectedPlayerId != null) {
                val receiverData = receiverRoster
                if (receiverData != null && receiverData.players.any { it.id == preselectedPlayerId }) {
                    selectedReceiverPlayerIds.add(preselectedPlayerId)
                }
            }
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load rosters"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val sender = senderRoster ?: return
    val receiver = receiverRoster ?: return

    val totalSenderAssets = selectedSenderPlayerIds.size + selectedSenderPickIds.size
    val totalReceiverAssets = selectedReceiverPlayerIds.size + selectedReceiverPickIds.size
    val canSubmit = totalSenderAssets > 0 && totalReceiverAssets > 0 && !submitting && !authState.user.isImpersonating

    val scope = rememberCoroutineScope()

    Column(Modifier.fillMaxWidth()) {
        // Back button
        SpanText(
            "\u2190 Back",
            Modifier
                .fontSize(0.875.cssRem)
                .color(SitePalette.textSecondary)
                .cursor(Cursor.Pointer)
                .margin(bottom = 1.cssRem)
                .onClick { ctx.router.tryRoutingTo("/clubhouse") }
        )

        // Sticky header
        Column(
            Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.background)
                .styleModifier {
                    property("position", "sticky")
                    property("top", "0")
                    property("z-index", "10")
                }
                .padding(bottom = 1.5.cssRem)
        ) {
            SpanText(
                "Create Trade Offer",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.75.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
                    .margin(bottom = 1.cssRem)
            )

            // Trade preview
            Column(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(SitePalette.surface)
                    .border(1.px, LineStyle.Solid, SitePalette.border)
                    .borderRadius(8.px)
                    .padding(1.cssRem)
            ) {
                // Desktop: side-by-side
                Row(Modifier.fillMaxWidth().displayIfAtLeast(Breakpoint.MD)) {
                    // You Send
                    Column(Modifier.styleModifier { property("flex", "1") }) {
                        SpanText(
                            "YOU SEND ($totalSenderAssets)",
                            Modifier
                                .fontSize(0.6875.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .color(SitePalette.textMuted)
                                .styleModifier { property("letter-spacing", "0.05em") }
                                .margin(bottom = 0.5.cssRem)
                        )
                        TradePreviewAssetList(sender, selectedSenderPlayerIds, selectedSenderPickIds)
                    }

                    // Swap icon
                    Div(
                        Modifier
                            .display(DisplayStyle.Flex)
                            .styleModifier {
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                            .padding(leftRight = 1.cssRem)
                            .toAttrs()
                    ) {
                        SpanText("⇄", Modifier.fontSize(1.25.cssRem).color(SitePalette.textMuted))
                    }

                    // You Receive
                    Column(Modifier.styleModifier { property("flex", "1") }) {
                        SpanText(
                            "YOU RECEIVE ($totalReceiverAssets)",
                            Modifier
                                .fontSize(0.6875.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .color(SitePalette.textMuted)
                                .styleModifier { property("letter-spacing", "0.05em") }
                                .margin(bottom = 0.5.cssRem)
                        )
                        TradePreviewAssetList(receiver, selectedReceiverPlayerIds, selectedReceiverPickIds)
                    }
                }

                // Mobile: stacked
                Column(Modifier.fillMaxWidth().displayUntil(Breakpoint.MD).gap(0.75.cssRem)) {
                    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        Column(Modifier.styleModifier { property("flex", "1") }) {
                            SpanText(
                                "YOU SEND ($totalSenderAssets)",
                                Modifier
                                    .fontSize(0.6875.cssRem)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(SitePalette.textMuted)
                                    .styleModifier { property("letter-spacing", "0.05em") }
                                    .margin(bottom = 0.375.cssRem)
                            )
                            TradePreviewAssetList(sender, selectedSenderPlayerIds, selectedSenderPickIds)
                        }
                        Div(
                            Modifier
                                .padding(leftRight = 0.75.cssRem)
                                .toAttrs()
                        ) {
                            SpanText("⇄", Modifier.fontSize(1.cssRem).color(SitePalette.textMuted))
                        }
                        Column(Modifier.styleModifier { property("flex", "1") }) {
                            SpanText(
                                "YOU RECEIVE ($totalReceiverAssets)",
                                Modifier
                                    .fontSize(0.6875.cssRem)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(SitePalette.textMuted)
                                    .styleModifier { property("letter-spacing", "0.05em") }
                                    .margin(bottom = 0.375.cssRem)
                            )
                            TradePreviewAssetList(receiver, selectedReceiverPlayerIds, selectedReceiverPickIds)
                        }
                    }
                }

                // Submit button + error
                Row(
                    Modifier.fillMaxWidth().margin(top = 0.75.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (submitError != null) {
                        SpanText(
                            submitError!!,
                            Modifier.fontSize(0.8125.cssRem).color(SitePalette.error)
                        )
                    }
                    Spacer()
                    Div(
                        Modifier
                            .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                            .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                            .borderRadius(8.px)
                            .padding(topBottom = 0.625.cssRem, leftRight = 1.25.cssRem)
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(if (canSubmit) Cursor.Pointer else Cursor.Default)
                            .onClick {
                                if (!canSubmit) return@onClick
                                submitError = null
                                submitting = true
                                scope.launch {
                                    try {
                                        val request = CreateTradeOfferRequest(
                                            receiverClubId = receiverClubId,
                                            senderAssets = selectedSenderPlayerIds.map { TradeAssetRequest(playerId = it) } +
                                                selectedSenderPickIds.map { TradeAssetRequest(draftPickId = it) },
                                            receiverAssets = selectedReceiverPlayerIds.map { TradeAssetRequest(playerId = it) } +
                                                selectedReceiverPickIds.map { TradeAssetRequest(draftPickId = it) },
                                        )
                                        apiPost(
                                            "/api/trades/create",
                                            apiJson.encodeToString(request),
                                            TradeOfferResponse.serializer(),
                                        )
                                        ctx.router.tryRoutingTo("/clubhouse")
                                    } catch (e: ApiException) {
                                        submitError = e.errorMessage
                                    } catch (e: Exception) {
                                        submitError = "Failed to create trade offer"
                                    }
                                    submitting = false
                                }
                            }
                            .toAttrs()
                    ) {
                        Text(if (submitting) "Sending..." else "Send Trade Offer")
                    }
                }
            }
        }

        // Roster sections
        Div(TradeRosterGridStyle.toAttrs()) {
            // Sender club roster
            TradeRosterColumn(
                clubName = sender.clubName,
                subtitle = "Tap players to include in offer",
                players = sender.players,
                picks = sender.draftPicks,
                selectedPlayerIds = selectedSenderPlayerIds,
                selectedPickIds = selectedSenderPickIds,
            )

            // Receiver club roster
            TradeRosterColumn(
                clubName = receiver.clubName,
                subtitle = "Tap players to request in trade",
                players = receiver.players,
                picks = receiver.draftPicks,
                selectedPlayerIds = selectedReceiverPlayerIds,
                selectedPickIds = selectedReceiverPickIds,
            )
        }
    }
}

@Composable
private fun TradePreviewAssetList(
    roster: ClubRosterResponse,
    selectedPlayerIds: List<String>,
    selectedPickIds: List<String>,
) {
    if (selectedPlayerIds.isEmpty() && selectedPickIds.isEmpty()) {
        SpanText(
            "No assets selected",
            Modifier.fontSize(0.75.cssRem).fontStyle(FontStyle.Italic).color(SitePalette.textMuted)
        )
        return
    }

    Column(Modifier.gap(0.25.cssRem)) {
        selectedPlayerIds.forEach { playerId ->
            val player = roster.players.find { it.id == playerId }
            if (player != null) {
                Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        player.name,
                        Modifier.fontSize(0.8125.cssRem).color(SitePalette.text)
                    )
                    SportBadge(player.sport, player.isLiv)
                    Spacer()
                    SpanText(
                        formatPoints(player.seasonPoints),
                        Modifier
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .color(SitePalette.accent)
                            .styleModifier { property("font-variant-numeric", "tabular-nums") }
                    )
                }
            }
        }
        selectedPickIds.forEach { pickId ->
            val pick = roster.draftPicks.find { it.id == pickId }
            if (pick != null) {
                val roundLabel = if (pick.round == "FIRST") "1st Rd" else "2nd Rd"
                val label = "${pick.year} $roundLabel" + if (pick.isTraded) " (${pick.originalOwnerName})" else ""
                SpanText(
                    label,
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.text)
                )
            }
        }
    }
}

@Composable
private fun TradeRosterColumn(
    clubName: String,
    subtitle: String,
    players: List<RosterPlayerResponse>,
    picks: List<DraftPickResponse>,
    selectedPlayerIds: MutableList<String>,
    selectedPickIds: MutableList<String>,
) {
    Column(Modifier.fillMaxWidth().gap(1.5.cssRem)) {
        // Club heading
        Column {
            SpanText(
                clubName,
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            SpanText(
                subtitle,
                Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted).margin(top = 0.25.cssRem)
            )
        }

        // Sport sections
        val sportGroups = mapOf(
            "MENS_GOLF" to players.filter { it.sport == "MENS_GOLF" },
            "MENS_TENNIS" to players.filter { it.sport == "MENS_TENNIS" },
            "WOMENS_TENNIS" to players.filter { it.sport == "WOMENS_TENNIS" },
        )

        sportGroups.forEach { (sport, sportPlayers) ->
            if (sportPlayers.isNotEmpty()) {
                Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    SpanText(
                        sportDisplayName(sport),
                        Modifier
                            .fontFamily(*HeadingFontFamily)
                            .fontSize(1.cssRem)
                            .fontWeight(FontWeight.Bold)
                            .color(SitePalette.text)
                    )
                    Div(TradePlayerGridStyle.toAttrs()) {
                        sportPlayers
                            .sortedWith(compareBy<RosterPlayerResponse> { it.rosterSlot == "LIV_LINE" }.thenByDescending { it.seasonPoints })
                            .forEach { player ->
                                val isSelected = player.id in selectedPlayerIds
                                TradePlayerCard(
                                    player = player,
                                    isSelected = isSelected,
                                    onClick = {
                                        if (isSelected) selectedPlayerIds.remove(player.id)
                                        else selectedPlayerIds.add(player.id)
                                    },
                                )
                            }
                    }
                }
            }
        }

        // Draft picks section
        if (picks.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                SpanText(
                    "Draft Picks",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )
                Div(
                    Modifier
                        .display(DisplayStyle.Flex)
                        .styleModifier { property("flex-wrap", "wrap") }
                        .gap(0.5.cssRem)
                        .fillMaxWidth()
                        .toAttrs()
                ) {
                    picks.sortedWith(compareBy({ it.year }, { it.round })).forEach { pick ->
                        val isSelected = pick.id in selectedPickIds
                        TradeDraftPickCard(
                            pick = pick,
                            isSelected = isSelected,
                            onClick = {
                                if (isSelected) selectedPickIds.remove(pick.id)
                                else selectedPickIds.add(pick.id)
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TradePlayerCard(
    player: RosterPlayerResponse,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    val isLiv = player.rosterSlot == "LIV_LINE"
    val leftBorderColor = when {
        isSelected -> SitePalette.primary
        isLiv -> SitePalette.error
        else -> SitePalette.border
    }

    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, leftBorderColor)
            .borderRadius(8.px)
            .padding(0.75.cssRem)
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .transition(Transition.of("border-color", 150.ms, TransitionTimingFunction.Ease))
    ) {
        // Top row: name + age + sport badge
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                player.name,
                Modifier
                    .fontSize(0.9375.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(SitePalette.text)
            )
            if (player.age != null) {
                SpanText(
                    "  ${player.age}",
                    Modifier
                        .fontSize(0.9375.cssRem)
                        .fontStyle(FontStyle.Italic)
                        .color(SitePalette.textMuted)
                )
            }
            Spacer()
            SportBadge(player.sport, player.isLiv)
        }

        // Season points
        Row(Modifier.margin(top = 0.25.cssRem), verticalAlignment = Alignment.Bottom) {
            SpanText(
                formatPoints(player.seasonPoints),
                Modifier
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.accent)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") }
            )
            SpanText(
                " season pts",
                Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted)
            )
        }

        if (isLiv) {
            SpanText(
                "LIV Line (inactive)",
                Modifier
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.error)
                    .margin(top = 0.5.cssRem)
            )
        }
    }
}

@Composable
private fun TradeDraftPickCard(
    pick: DraftPickResponse,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    val roundLabel = if (pick.round == "FIRST") "1st Round" else "2nd Round"

    Column(
        Modifier
            .styleModifier { property("min-width", "140px") }
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, if (isSelected) SitePalette.primary else SitePalette.border)
            .borderRadius(8.px)
            .padding(0.5.cssRem, 0.75.cssRem)
            .gap(0.125.cssRem)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .transition(Transition.of("border-color", 150.ms, TransitionTimingFunction.Ease))
    ) {
        SpanText(
            "${pick.year}",
            Modifier
                .fontSize(0.75.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textSecondary)
        )
        SpanText(
            roundLabel,
            Modifier
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.accent)
        )
        if (pick.isTraded) {
            SpanText(
                "from ${pick.originalOwnerName}",
                Modifier
                    .fontSize(0.6875.cssRem)
                    .color(SitePalette.textMuted)
            )
        }
    }
}
